import * as React from "react";
import {gql} from "@apollo/client";
import {query} from "../../lib/apollo";
import Loading from "../loading";
import {
    Alert,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
} from "@mui/material";
import Box from "@mui/material/Box";
import DateRange, {RangeToTimes} from "../DateRange";
import moment from "moment";
import TableHeaderRow from "../TableHeaderRow";
import StripedTableRow from "../StripedTableRow";
import TableFooterRow from "../TableFooterRow";
import MultiSelect from "../multiSelect/multiSelect";
import {searchPubs} from "../../pages/blockedPublishers/blockedPublishersService";
import CachedIcon from "@mui/icons-material/Cached";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import Timeseries from "../timeseries";
import {toast} from "react-toastify";

const COLORS = [
    "#b20000", "#a0607e", "#a0609c", "#6960a0", "#5f7aa8", "#5f97a8", "#0087b0",
    "#01b1a5", "#7ab294", "#00b218", "#000000", "#6d6d6d", "#6cb103", "#99b273",
    "#a9b273", "#95b200", "#b29b00", "#b27d00", "#b19b66", "#b18966", "#b25300",
];

function dimMeta(dimension: string) {
    if (dimension === "when_start") {
        return {d1: "when_start", d2: "keyword", header: "When Start", sortAge: false};
    }
    if (dimension === "age") {
        return {d1: "age", d2: "keyword", header: "Age", sortAge: true};
    }
    if (dimension === "degree_level") {
        return {d1: "degree_level", d2: "keyword", header: "Degree Seeking", sortAge: false};
    }
    if (dimension === "edu_level") {
        return {d1: "edu_level", d2: "keyword", header: "Education Level", sortAge: false};
    }
    return {d1: "subject", d2: "program", header: "Subject", sortAge: false};
}

function col(row: any, key: string): string {
    const c = (row.columns || []).find((x: any) => x.dataType === key);
    return c ? c.value : "";
}

function nest(rows: any[], d1: string, d2: string) {
    const groups: any[] = [];
    let grand = 0;
    let scrubSum = 0;
    for (const row of rows || []) {
        const label = col(row, d1);
        let g = groups.find((x) => x.label === label);
        if (!g) {
            g = {label, total: 0, scrub: 0, percent: 0, other: 0, other_percent: 0, children: []};
            groups.push(g);
        }
        g.total += row.total;
        g.scrub += (row.scrub || 0) * row.total;
        g.children.push({
            label: col(row, d2),
            total: row.total,
            scrub: row.scrub || 0,
            percent: 0,
            other: 0,
            other_percent: 0,
        });
        grand += row.total;
        scrubSum += (row.scrub || 0) * row.total;
    }
    for (const g of groups) {
        g.scrub = g.total ? g.scrub / g.total : 0;
        g.percent = grand ? g.total / grand * 100 : 0;
        g.children.forEach((c: any) => {
            c.percent = grand ? c.total / grand * 100 : 0;
        });
        g.children.sort((a: any, b: any) => b.total - a.total);
    }
    return {groups, total: grand, scrub: grand ? scrubSum / grand : 0};
}

function mergeOthers(selected: any, others: any) {
    for (const g of selected.groups) {
        const o = others.groups.find((x: any) => x.label === g.label);
        if (!o) {
            continue;
        }
        g.other = o.total;
        g.other_percent = o.percent;
        for (const c of g.children) {
            const oc = o.children.find((x: any) => x.label === c.label);
            if (oc) {
                c.other = oc.total;
                c.other_percent = oc.percent;
            }
        }
    }
    return {...selected, totalOther: others.total};
}

function dateFormatForRange(date: any[]) {
    const days = moment(date[1]).diff(moment(date[0]), "days");
    if (days < 3) {
        return "M/D ha";
    }
    if (days < 70) {
        return "M/D";
    }
    return "MMM YYYY";
}

function toChart(ts: any, mode: string) {
    const series: any[] = [];
    let colIdx = 0;
    let maxEpc = 0;
    series.push({
        name: "EPC",
        data: (ts.epc || []).map((p: any) => {
            if (p.value > maxEpc) {
                maxEpc = p.value;
            }
            return [p.timestamp, p.value];
        }),
        visible: false,
        yAxis: 1,
        dashStyle: "ShortDashDotDot",
        color: COLORS[colIdx++],
    });

    const addGroup = (items: any[], prefix: string, dashStyle?: string) => {
        const totals: Record<string, number> = {};
        if (mode === "Percent") {
            for (const s of items || []) {
                for (const p of s.points || []) {
                    totals[p.timestamp] = (totals[p.timestamp] || 0) + p.value;
                }
            }
        }
        for (const s of items || []) {
            series.push({
                name: prefix + s.label,
                visible: false,
                dashStyle,
                color: COLORS[colIdx++ % COLORS.length],
                data: (s.points || []).map((p: any) => {
                    let y = p.value;
                    if (mode === "Percent") {
                        y = totals[p.timestamp] ? Math.round(y / totals[p.timestamp] * 1000) / 10 : 0;
                    }
                    return [p.timestamp, y];
                }),
            });
        }
    };

    addGroup(ts.series || [], "");
    addGroup(ts.others || [], "Other Pubs: ", "ShortDash");

    return {
        series,
        yAxisRight: {
            title: {text: "EPC"},
            min: 0,
            max: Math.ceil(maxEpc * 1.1) || 6,
            opposite: true,
        },
    };
}

export default function PubInventory(params: {pubId: number, inventory: any, setInventory: any}) {
    React.useEffect(() => {
        if (!params.inventory.dateRange.length) {
            load(RangeToTimes("Today"), params.inventory.dimension, params.inventory.selectedPublishers);
        }
    }, []);

    const load = async (date: any, dimension = params.inventory.dimension, publisherSel = params.inventory.selectedPublishers) => {
        const pubIds = (publisherSel || []).map((x: any) => x.id);
        if (!pubIds.length && params.pubId) {
            pubIds.push(params.pubId);
        }
        if (!pubIds.length) {
            toast.error("Select at least one publisher");
            return;
        }
        const meta = dimMeta(dimension);
        params.setInventory({
            ...params.inventory,
            loading: true,
            dateRange: date,
            dimension,
            selectedPublishers: publisherSel,
            d1Header: meta.header,
        });

        const statsQ = gql`
            query q($input: InventoryStatInput!) {
                inventoryStats(input: $input) {
                    columns { dataType value }
                    total
                    scrub
                }
            }
        `;
        const startDate = moment(date[0]).format("YYYY-MM-DD HH:mm:ss");
        const endDate = moment(date[1]).format("YYYY-MM-DD HH:mm:ss");
        const data = [meta.d1, meta.d2];

        const selected = await query(statsQ, {input: {startDate, endDate, data, publisherIds: pubIds}});
        const others = await query(statsQ, {input: {startDate, endDate, data, excludePublisherIds: pubIds}});

        let nested = nest(selected?.inventoryStats || [], meta.d1, meta.d2);
        nested = mergeOthers(nested, nest(others?.inventoryStats || [], meta.d1, meta.d2));
        if (meta.sortAge) {
            nested.groups.sort((a: any, b: any) => String(a.label).localeCompare(String(b.label), undefined, {numeric: true}));
        } else {
            nested.groups.sort((a: any, b: any) => b.total - a.total);
        }

        const chartQ = gql`
            query q($input: InventoryPubTimeseriesInput!) {
                inventoryPubTimeseries(input: $input) {
                    epc { timestamp value }
                    series { label points { timestamp value } }
                    others { label points { timestamp value } }
                }
            }
        `;
        const chartRes = await query(chartQ, {
            input: {startDate, endDate, publisherIds: pubIds, data: dimension},
        });
        const ts = chartRes?.inventoryPubTimeseries || {epc: [], series: [], others: []};
        const graphMode = params.inventory.graphMode || "Count";
        const chart = toChart(ts, graphMode);

        params.setInventory({
            ...params.inventory,
            loading: false,
            dateRange: date,
            dimension,
            selectedPublishers: publisherSel,
            d1Header: meta.header,
            data: nested,
            timeseries: ts,
            series: chart.series,
            yAxisRight: chart.yAxisRight,
            dateFormat: dateFormatForRange(date),
            expanded: {},
            graphMode,
        });
    };

    const setSelectedPublishers = (items: any[]) => {
        params.setInventory({...params.inventory, selectedPublishers: items});
    };

    const setDimension = (dimension: string) => {
        load(params.inventory.dateRange, dimension);
    };

    const setGraphMode = (graphMode: string) => {
        const chart = toChart(params.inventory.timeseries || {epc: [], series: [], others: []}, graphMode);
        params.setInventory({
            ...params.inventory,
            graphMode,
            series: chart.series,
            yAxisRight: chart.yAxisRight,
        });
    };

    const toggle = (label: string) => {
        params.setInventory({
            ...params.inventory,
            expanded: {...(params.inventory.expanded || {}), [label]: !params.inventory.expanded?.[label]},
        });
    };

    if (params.inventory.loading || !params.inventory.dateRange.length) {
        return <Box sx={{padding: 3}}><Loading/></Box>;
    }

    const rows = params.inventory.data?.groups || [];
    const totals = params.inventory.data || {total: 0, scrub: 0, totalOther: 0};

    return (
        <Box sx={{padding: "0 10px 10px 10px"}}>
            <Box sx={{display: "flex", flexDirection: "row", margin: 0, padding: 1, flexFlow: "wrap", alignItems: "flex-end"}}>
                <DateRange
                    showTime={true}
                    dateRange={params.inventory.dateRange}
                    setDateRange={(value: any) => load(value)}
                    style={{marginBottom: "15px", marginRight: "10px"}}
                />
                <FormControl size="small" sx={{minWidth: 180, marginRight: 1, marginBottom: "15px"}}>
                    <InputLabel>Data</InputLabel>
                    <Select
                        value={params.inventory.dimension}
                        label="Data"
                        onChange={(e) => setDimension(e.target.value as string)}
                    >
                        <MenuItem value="subject">Subject</MenuItem>
                        <MenuItem value="when_start">When Start</MenuItem>
                        <MenuItem value="age">Age</MenuItem>
                        <MenuItem value="degree_level">Degree Seeking</MenuItem>
                        <MenuItem value="edu_level">Education Level</MenuItem>
                    </Select>
                </FormControl>
                <Box sx={{width: "350px", marginLeft: "auto", display: "flex"}}>
                    <MultiSelect
                        searchFunction={searchPubs}
                        selectedItems={params.inventory.selectedPublishers || []}
                        setSelectedItems={setSelectedPublishers}
                        placeholder={"Select Publishers"}
                    />
                    <CachedIcon
                        sx={{cursor: "pointer", margin: "5px 0 0 5px"}}
                        onClick={() => load(params.inventory.dateRange)}
                        color={"success"}
                    />
                </Box>
            </Box>

            {!rows.length ? <Alert severity="warning">No results found.</Alert> :
                <TableContainer component={Paper} sx={{maxWidth: "calc(100vw - 20px)"}}>
                    <Table size="small">
                        <TableHead>
                            <TableHeaderRow>
                                <TableCell sx={{width: 40}}/>
                                <TableCell>{params.inventory.d1Header}</TableCell>
                                <TableCell align="right">Scrub</TableCell>
                                <TableCell align="right">Total</TableCell>
                                <TableCell align="right">Percent</TableCell>
                                <TableCell align="right">Others</TableCell>
                                <TableCell align="right">Others %</TableCell>
                            </TableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row: any) => (
                                <React.Fragment key={row.label}>
                                    <StripedTableRow>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => toggle(row.label)}>
                                                {params.inventory.expanded?.[row.label]
                                                    ? <IndeterminateCheckBoxOutlinedIcon sx={{fontSize: "20px"}}/>
                                                    : <AddBoxOutlinedIcon sx={{fontSize: "20px"}}/>}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{row.label}</TableCell>
                                        <TableCell align="right">{Number(row.scrub).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%</TableCell>
                                        <TableCell align="right">{row.total.toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                                        <TableCell align="right">{Number(row.percent).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%</TableCell>
                                        <TableCell align="right">{Number(row.other || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                                        <TableCell align="right">{Number(row.other_percent || 0).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%</TableCell>
                                    </StripedTableRow>
                                    {params.inventory.expanded?.[row.label] && row.children.map((c: any) => (
                                        <StripedTableRow key={`${row.label}-${c.label}`}>
                                            <TableCell/>
                                            <TableCell>{c.label}</TableCell>
                                            <TableCell align="right">{Number(c.scrub).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%</TableCell>
                                            <TableCell align="right">{c.total.toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                                            <TableCell align="right">{Number(c.percent).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%</TableCell>
                                            <TableCell align="right">{Number(c.other || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                                            <TableCell align="right">{Number(c.other_percent || 0).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%</TableCell>
                                        </StripedTableRow>
                                    ))}
                                </React.Fragment>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableFooterRow>
                                <TableCell colSpan={2}>Totals</TableCell>
                                <TableCell align="right">{Number(totals.scrub).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%</TableCell>
                                <TableCell align="right">{Number(totals.total).toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                                <TableCell align="right">100%</TableCell>
                                <TableCell align="right">{Number(totals.totalOther || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                                <TableCell align="right">100%</TableCell>
                            </TableFooterRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            }

            <Box sx={{display: "flex", justifyContent: "center", marginTop: 2, marginBottom: 1}}>
                <FormControl size="small" sx={{minWidth: 120}}>
                    <Select
                        value={params.inventory.graphMode || "Count"}
                        onChange={(e) => setGraphMode(e.target.value as string)}
                    >
                        <MenuItem value="Count">Count</MenuItem>
                        <MenuItem value="Percent">Percent</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            {!!params.inventory.series?.length && (
                <Timeseries
                    height={400}
                    series={params.inventory.series}
                    title={params.inventory.d1Header}
                    prefix=""
                    suffix={params.inventory.graphMode === "Percent" ? "%" : ""}
                    dateFormat={params.inventory.dateFormat || "M/D"}
                    decimalPlaces={params.inventory.graphMode === "Percent" ? 1 : 0}
                    yAxisRight={params.inventory.yAxisRight}
                />
            )}
        </Box>
    );
}
